import api from "./axiosConfig"; // Importa la configuración centralizada de axios
import { apiRegister } from "./authService";

// Obtener todos los clientes.
// Req 1 (2026-05-13): acepta filtros opcionales para facturación de terceros:
//   { es_emisor, es_tercero, padre_id, activo }
// Devuelve la respuesta tal cual del backend:
//   - admin/auditor   → { data: [...], page, per_page, total, ... }
//   - operador/otros  → [...]  (array plano)
// Los consumidores deben usar el helper `normalizeClientsResponse` o
// chequear ambos formatos.
export const getClients = async (filters = {}) => {
  try {
    const authData = localStorage.getItem("authData");
    if (authData) {
      const { rol } = JSON.parse(authData);
      const params = {};
      if (filters.es_emisor != null) params.es_emisor = String(filters.es_emisor);
      if (filters.es_tercero != null) params.es_tercero = String(filters.es_tercero);
      if (filters.padre_id != null) params.padre_id = filters.padre_id;
      if (filters.activo != null) params.activo = String(filters.activo);
      // (2026-05-13) Excluye terceros que ya tienen contrato Pendiente o Aprobado.
      if (filters.sin_contrato_activo != null) params.sin_contrato_activo = String(filters.sin_contrato_activo);
      var response;
      if (rol == 'admin' || rol == 'auditor'){
        // (2026-08-02, ítem 21 — HALLAZGO P1) /admin/empresas pagina SIEMPRE
        // (admin_routes.py:146-153) con DEFAULT_PER_PAGE=20: los desplegables
        // de emisores/terceros y la propia lista de empresas se truncaban a 20
        // filas SIN ninguna señal (un buscador sobre una lista truncada miente).
        // Cuando el llamador no pide paginación explícita se solicita el lote
        // completo: request_type='export' eleva el tope a MAX_PER_PAGE_EXPORT
        // (5000, admin_routes.py:6) y per_page se fija a ese máximo.
        if (filters.page != null) params.page = filters.page;
        if (filters.per_page != null) params.per_page = filters.per_page;
        if (filters.request_type != null) params.request_type = filters.request_type;
        if (params.per_page == null && params.request_type == null) {
          params.request_type = 'export';
          params.per_page = 5000;
        }
        response = await api.get('/admin/empresas', { params });
      }else{
        // /api/empresas/ NO pagina: devuelve array plano (empresa_routes.py:
        // 635-636, :644-652), por lo que no necesita per_page.
        response = await api.get('/api/empresas/', { params });
      }
      return response.data;
    }
  } catch (error) {
    console.error("Error al obtener los clientes:", error);
    throw error;
  }
};

// Helper: normaliza la respuesta de getClients a un array plano.
// Tolera ambos formatos: array directo o { data: [...] } paginado.
export const normalizeClientsResponse = (resp) => {
  if (Array.isArray(resp)) return resp;
  if (resp && Array.isArray(resp.data)) return resp.data;
  return [];
};

// Consultar un cliente por ID
export const showClient = async (id) => {
  try {
    const authData = localStorage.getItem("authData");
    if (authData) {
      const { rol } = JSON.parse(authData);
      var response;
      if (rol == 'admin' || rol == 'auditor'){
        response = await api.get(`/admin/empresas/${id}`);
      }else{
        response = await api.get(`/api/empresas/${id}`);
      }
      var resLogo = await getClientLogo(response.data.id);
      if (resLogo){
        response.data.logo = resLogo;
      }
      return response.data;
    }
  } catch (error) {
    console.error("Error al consultar el cliente:", error);
    throw error;
  }
};

// Editar un cliente
export const editClient = async (id, body) => {
  try {
    const authData = localStorage.getItem("authData");
    if (authData) {
      const { rol } = JSON.parse(authData);
      var response;
      // Extraer logo y preparar objeto con content_type
      let logoPayload = null;
      if (body.logo) {
        // Extraer tipo MIME desde el base64
        const match = body.logo.match(/^data:(image\/[a-zA-Z]+);base64,/);
        const content_type = match ? match[1] : "image/png";
        logoPayload = {
          logo: body.logo,
          content_type
        };
        // Eliminar logo del body principal para no enviarlo duplicado
        delete body.logo;
      }
      if (rol == 'admin' || rol == 'auditor'){
        response = await api.put(`/admin/empresas/${id}`, body);
      }else{
        response = await api.put(`/api/empresas/${id}`, body);
      }
      if (logoPayload) {
        await api.put(`/api/empresas/${id}/logo`, logoPayload);
      }

      return response.data;
    }
  } catch (error) {
    console.error("Error al editar el cliente:", error);
    throw error;
  }
};

// Crear un cliente
export const createClient = async (body) => {
  try {
    //console.log('createClient-body: ', body);
    const response = await api.post(`/api/empresas/`, body);
    //console.log('createClient-response: ', response);
    for (var i = 0; i < body.length; i++){
      // (2026-05-22) FIX: las empresas de TERCERO no entran en el
      // esquema de empresas directas del sistema — no inician sesion
      // (las gestiona su emisor), por lo que NO se les debe crear un
      // usuario inicial. Solo las empresas directas reciben usuario.
      if (body[i] && body[i].es_tercero) {
        continue;
      }
      var bodyUser = {
        "nombre": body[i].nombre_empresa,
        "email": body[i].email,
        "password": body[i].rif,
        "empresa_id": response.data[i].id,
      }
      //console.log('createClient-bodyUser: ', bodyUser);
      var responseUser = await apiRegister(bodyUser);
      //console.log('createClient-responseUser: ', responseUser);
    }
    return response.data;
  } catch (error) {
    console.error("Error al crear el cliente:", error);
    throw error;
  }
};

// Desactivar un cliente
export const deleteClient = async (id) => {
  try {
    const response = await api.delete(`/api/empresas/${id}`);
    console.log('deleteClient-response: ', response);
    return response.data;
  } catch (error) {
    console.error("Error al eliminar el cliente:", error);
    throw error;
  }
};

// (2026-05-22) Correlativos fiscales (correlativos_sistema) de una empresa.
// Endpoints en client_bp (/api/empresas). admin gestiona cualquiera,
// operador_admin solo la propia (el backend valida el permiso).
export const getCorrelativosEmpresa = async (empresaId) => {
  try {
    const response = await api.get(`/api/empresas/${empresaId}/correlativos`);
    const body = response.data;
    return Array.isArray(body) ? body : (body?.data || []);
  } catch (error) {
    console.error("Error al obtener correlativos de la empresa:", error);
    throw error;
  }
};

export const saveCorrelativosEmpresa = async (empresaId, lista) => {
  try {
    const response = await api.put(`/api/empresas/${empresaId}/correlativos`, lista);
    return response.data;
  } catch (error) {
    console.error("Error al guardar correlativos de la empresa:", error);
    throw error;
  }
};

// Activar un cliente
export const activateClient = async (id) => {
  try {
    const response = await api.put(`/api/empresas/${id}/activar`);
    console.log('deleteClient-response: ', response);
    return response.data;
  } catch (error) {
    console.error("Error al eliminar el cliente:", error);
    throw error;
  }
};

export const getClientLogo = async (id) => {
  try {
    const response = await api.get(`/api/empresas/${id}/logo`, {
      responseType: "arraybuffer" // muy importante para manejar binarios
    });

    // Convertir a base64 para usar en jsPDF
    const base64 = btoa(
      new Uint8Array(response.data).reduce(
        (data, byte) => data + String.fromCharCode(byte),
        ""
      )
    );

    // Detectar tipo de imagen (puedes ajustarlo si sabes que siempre es PNG)
    return `data:image/png;base64,${base64}`;
  } catch (err) {
    // (2026-05-17) FIND-6: el logo es OPCIONAL. La ausencia/permiso del
    // logo devuelve 4xx (404 sin logo, 403 sin acceso, etc.) — son casos
    // ESPERADOS y no deben ensuciar la consola. Solo se loguea una falla
    // real: sin respuesta (red) o error de servidor (5xx).
    const _st = err?.response?.status;
    if (!_st || _st >= 500) {
      console.error("Error al obtener logo del cliente:", err);
    }
    return null;
  }
};